"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTranslations } from "next-intl";
import { Check, ChevronDown, Download, FileText, Printer } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { PdfAction } from "./pdf-delivery";
import type { PdfRefusal } from "./refusal";
import type { PdfRun } from "./use-pdf-run";

/**
 * The problem set PDF's controls that every surface shares (`EX-PDF02` drew
 * them for the organizer console; `EX-PDF03` lifted them here for Author
 * Studio): the status line under a control, the dialog's footer, and the
 * one-problem menu. Their words are `contest.problemSetPdf`'s.
 */

/** A refusal said in words: the busy printer with how long to wait, and the rest plainly. */
export function usePdfRefusalText() {
  const t = useTranslations("contest.problemSetPdf.refusal");
  return (refusal: PdfRefusal) => (refusal.key === "busy" ? t("busy", { seconds: refusal.seconds }) : t(refusal.key));
}

/** What a finished PDF says: saved, or printing from its own tab (or from this page when the tab was blocked). */
export function doneLine(
  t: (key: "doneSaved" | "doneTab" | "doneFrame") => string,
  run: Extract<PdfRun, { status: "done" }>,
): string {
  return run.delivery === "saved" ? t("doneSaved") : run.delivery === "tab" ? t("doneTab") : t("doneFrame");
}

/** The status line's colours on a light card, and on a dark one. */
const STATUS_TONE = {
  light: {
    making: "text-neutral-600",
    error: "text-red-700",
    done: "text-neutral-700",
    check: "text-emerald-700",
    file: "text-neutral-500",
  },
  dark: {
    making: "text-neutral-300",
    error: "text-red-300",
    done: "text-neutral-200",
    check: "text-emerald-300",
    file: "text-neutral-400",
  },
} as const;

/**
 * The line under a PDF control: being made, done (saved, or printing in its
 * own tab), or the refusal in words. Nothing while idle. `tone="dark"` is the
 * same line on the contest page's dark door (`EX-PDF05`).
 */
export function PdfRunStatus({
  run,
  className,
  tone = "light",
}: {
  run: PdfRun;
  className?: string;
  tone?: keyof typeof STATUS_TONE;
}) {
  const t = useTranslations("contest.problemSetPdf");
  const refusalText = usePdfRefusalText();
  const colours = STATUS_TONE[tone];

  if (run.status === "idle") return null;
  if (run.status === "making") {
    return (
      <p role="status" className={cn("flex items-center gap-2 text-xs", colours.making, className)}>
        <span aria-hidden className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
        <span>{t("making")}</span>
      </p>
    );
  }
  if (run.status === "error") {
    return (
      <p role="alert" className={cn("text-xs leading-relaxed", colours.error, className)}>
        {refusalText(run.refusal)}
      </p>
    );
  }
  return (
    <p role="status" className={cn("flex min-w-0 items-center gap-2 text-xs", colours.done, className)}>
      <Check aria-hidden className={cn("h-3.5 w-3.5 shrink-0", colours.check)} />
      <span className="min-w-0">
        <span className="font-semibold">{doneLine(t, run)}</span>
        <span className={cn("ml-1.5 break-all font-mono text-[11px]", colours.file)}>{run.filename}</span>
      </span>
    </p>
  );
}

/**
 * An options dialog's footer: the summary with Print and Download PDF, then
 * the making bar, then done with the file's name, and a refusal in words
 * above the buttons.
 */
export function PdfDialogFooter({
  run,
  count,
  summary,
  emptyLine,
  onPrint,
  onDownload,
  onDone,
}: {
  run: PdfRun;
  /** How many problems the PDF holds; none keeps the buttons off. */
  count: number;
  summary: string;
  /** What the footer says when nothing is picked. */
  emptyLine: string;
  onPrint: () => void;
  onDownload: () => void;
  onDone: () => void;
}) {
  const t = useTranslations("contest.problemSetPdf");
  const refusalText = usePdfRefusalText();

  if (run.status === "making") {
    return (
      <div role="status" className="space-y-2">
        <p className="text-sm font-medium text-neutral-900">{t("makingCount", { count })}</p>
        <div className="h-1.5 overflow-hidden rounded-full bg-neutral-200">
          <div className="h-1.5 w-2/5 animate-pulse rounded-full bg-primary" />
        </div>
      </div>
    );
  }

  if (run.status === "done") {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="status" className="flex min-w-0 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <Check aria-hidden className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-neutral-900">{doneLine(t, run)}</span>
            <span className="block break-all font-mono text-xs text-neutral-600">{run.filename}</span>
          </span>
        </div>
        <Button type="button" variant="outline" className="h-11" onClick={onDone} data-autofocus>
          {t("dialog.done")}
        </Button>
      </div>
    );
  }

  const none = count === 0;
  return (
    <div className="space-y-3">
      {run.status === "error" ? (
        <p role="alert" className="text-xs leading-relaxed text-red-700">
          {refusalText(run.refusal)}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-neutral-600">{none ? emptyLine : summary}</p>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button type="button" variant="outline" className="h-11" disabled={none} onClick={onPrint}>
            <Printer aria-hidden className="h-4 w-4" />
            <span>{t("dialog.print")}</span>
          </Button>
          <Button type="button" className="h-11" disabled={none} onClick={onDownload}>
            <Download aria-hidden className="h-4 w-4" />
            <span>{t("dialog.download")}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * One problem's PDF menu: Download PDF and Print, no cover. The trigger is a
 * square icon on a list row, or `text` beside the icon where there is room (a
 * page's header).
 */
export function PdfMenu({
  label,
  heading,
  disabled,
  onPick,
  text,
  className,
}: {
  /** The trigger's accessible name. */
  label: string;
  /** The menu's first line: what it prints. */
  heading: string;
  disabled: boolean;
  onPick: (action: PdfAction) => void;
  /** A visible label on the trigger; without it the trigger is an icon. */
  text?: string;
  className?: string;
}) {
  const t = useTranslations("contest.problemSetPdf.row");
  const itemClass =
    "flex min-h-11 cursor-pointer select-none items-center gap-2.5 rounded-lg px-3 text-sm text-neutral-900 outline-none transition-colors duration-150 ease-out data-[highlighted]:bg-neutral-100";
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild disabled={disabled}>
        {text ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn("h-11 text-primary sm:h-9", className)}
            aria-label={label}
          >
            <FileText aria-hidden className="h-4 w-4" />
            <span>{text}</span>
            <ChevronDown aria-hidden className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="smIcon"
            className={cn("h-11 w-11 text-primary sm:h-9 sm:w-9", className)}
            aria-label={label}
          >
            <FileText aria-hidden className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-[60] min-w-[240px] max-w-[320px] rounded-xl border border-neutral-200 bg-white p-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
        >
          <DropdownMenu.Label className="px-3 pb-1.5 pt-2 text-xs text-neutral-500">{heading}</DropdownMenu.Label>
          <DropdownMenu.Item className={itemClass} onSelect={() => onPick("download")}>
            <Download aria-hidden className="h-4 w-4" />
            {t("download")}
          </DropdownMenu.Item>
          <DropdownMenu.Item className={itemClass} onSelect={() => onPick("print")}>
            <Printer aria-hidden className="h-4 w-4" />
            {t("print")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
